import { vmixPresetsAPI } from './api';

// Script names for the cue editor's ScriptStart/ScriptStop dropdown.
//
// vMix has no API that lists the scripts loaded in a running instance — the
// script list exists only inside the .vmix preset file (see the note on
// POST /api/vmix/script-run). So the names offered here come from the preset
// library, i.e. whatever the operator uploaded in Tools → vMix Script Editor.
// That means the dropdown is a convenience, never a constraint: a cue may name
// a script from a preset nobody has uploaded, and that value must survive.
//
// Both fetches are memoised per page load. The cue editor opens and closes on
// every row, and the library changes about as often as a preset is re-uploaded.

let presetsPromise = null;
const scriptsPromises = new Map();

/** The workspace's preset library, newest-updated first. */
export function loadPresets() {
  if (!presetsPromise) {
    presetsPromise = vmixPresetsAPI.list()
      .then(r => (Array.isArray(r.data) ? r.data : []))
      .catch(() => {
        presetsPromise = null;   // a failed load shouldn't be cached as "empty"
        return [];
      });
  }
  return presetsPromise;
}

/** Script names in one preset, in preset order. Names are not unique. */
export function loadScriptNames(presetId) {
  if (!scriptsPromises.has(presetId)) {
    scriptsPromises.set(
      presetId,
      vmixPresetsAPI.scripts(presetId)
        .then(r => (r.data?.scripts || []).map(s => s.name))
        .catch(() => {
          scriptsPromises.delete(presetId);
          return [];
        })
    );
  }
  return scriptsPromises.get(presetId);
}

/** Drop the caches — call after uploading or editing a preset. */
export function invalidatePresetScripts() {
  presetsPromise = null;
  scriptsPromises.clear();
}

// Which preset the operator last picked scripts from. Persisted because a
// production runs against one preset for hours, and re-choosing it on every cue
// would be the whole point of the dropdown, lost.
const LS_KEY = 'mc-vmix-script-preset';

export function recallPresetId() {
  try { return localStorage.getItem(LS_KEY) || ''; } catch { return ''; }
}

export function rememberPresetId(id) {
  try { localStorage.setItem(LS_KEY, id); } catch { /* private mode */ }
}
